// Formats an email as a client-note body ("Save as client note" action). Pure, no
// network — the caller (useEmailActions) hands this straight to api.createAccountNote.

const MAX_BODY_CHARS = 20_000;

export interface EmailForNote {
  subject?: string | null;
  fromName?: string | null;
  fromAddress?: string | null;
  receivedDateTime?: string | null;
  bodyText?: string | null;
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, "");
}

function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/New_York" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) +
    " ET"
  );
}

function formatBody(bodyText: string | null | undefined): string {
  let text = String(bodyText ?? "");
  // Strip <script>/<style> blocks INCLUDING their contents before generic tag-stripping,
  // so inline JS/CSS text doesn't leak into the saved note body.
  text = text.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?<\/style>/gi, "");
  if (looksLikeHtml(text)) {
    text = stripHtml(text)
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ");
  }
  // Collapse 3+ blank lines to 2 (one blank line between paragraphs).
  text = text.replace(/\n{3,}/g, "\n\n").trim();
  if (text.length > MAX_BODY_CHARS) {
    text = text.slice(0, MAX_BODY_CHARS) + "\n… [truncated]";
  }
  return text;
}

export function formatEmailAsNote(email: EmailForNote): { title: string; plain_text: string } {
  const subject = (email.subject ?? "").trim();
  const title = subject ? `Email: ${subject}` : "Email: (no subject)";

  const fromName = (email.fromName ?? "").trim();
  const fromAddress = (email.fromAddress ?? "").trim();
  let fromLine: string;
  if (fromName && fromAddress) fromLine = `${fromName} <${fromAddress}>`;
  else if (fromName) fromLine = fromName;
  else if (fromAddress) fromLine = fromAddress;
  else fromLine = "Unknown sender";

  const dateStr = formatDate(email.receivedDateTime);

  const headerLines = [`From: ${fromLine}`];
  if (dateStr) headerLines.push(`Date: ${dateStr}`);

  const body = formatBody(email.bodyText);

  const plain_text = `${headerLines.join("\n")}\n\n${body}`;

  return { title, plain_text };
}
